import { Globe, Wrench, Palette, Briefcase, MoreHorizontal } from "lucide-react";

interface CategorySelectorProps {
  value: string;
  onChange: (value: string) => void;
}

const categories = [
  {
    value: "website_change",
    label: "Website Change",
    description: "Updates, edits, or modifications to your existing site",
    icon: Globe,
    accent: "group-data-[selected=true]:border-blue-500 group-data-[selected=true]:bg-blue-50 dark:group-data-[selected=true]:bg-blue-950/30",
    iconAccent: "group-data-[selected=true]:bg-blue-500/10 group-data-[selected=true]:text-blue-600 dark:group-data-[selected=true]:text-blue-400",
  },
  {
    value: "tech_support",
    label: "Tech Support",
    description: "Bug fixes, errors, or technical issues that need resolving",
    icon: Wrench,
    accent: "group-data-[selected=true]:border-orange-500 group-data-[selected=true]:bg-orange-50 dark:group-data-[selected=true]:bg-orange-950/30",
    iconAccent: "group-data-[selected=true]:bg-orange-500/10 group-data-[selected=true]:text-orange-600 dark:group-data-[selected=true]:text-orange-400",
  },
  {
    value: "creative_design",
    label: "Creative / Design",
    description: "Graphics, logos, visual assets, or branding work",
    icon: Palette,
    accent: "group-data-[selected=true]:border-purple-500 group-data-[selected=true]:bg-purple-50 dark:group-data-[selected=true]:bg-purple-950/30",
    iconAccent: "group-data-[selected=true]:bg-purple-500/10 group-data-[selected=true]:text-purple-600 dark:group-data-[selected=true]:text-purple-400",
  },
  {
    value: "new_project",
    label: "New Project",
    description: "A new website, app, or large-scale build from scratch",
    icon: Briefcase,
    accent: "group-data-[selected=true]:border-emerald-500 group-data-[selected=true]:bg-emerald-50 dark:group-data-[selected=true]:bg-emerald-950/30",
    iconAccent: "group-data-[selected=true]:bg-emerald-500/10 group-data-[selected=true]:text-emerald-600 dark:group-data-[selected=true]:text-emerald-400",
  },
  {
    value: "other",
    label: "Something Else",
    description: "Anything that doesn't fit the categories above",
    icon: MoreHorizontal,
    accent: "group-data-[selected=true]:border-slate-500 group-data-[selected=true]:bg-slate-50 dark:group-data-[selected=true]:bg-slate-950/30",
    iconAccent: "group-data-[selected=true]:bg-slate-500/10 group-data-[selected=true]:text-slate-600 dark:group-data-[selected=true]:text-slate-400",
  },
];

export function CategorySelector({ value, onChange }: CategorySelectorProps) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
      {categories.map((cat) => {
        const Icon = cat.icon;
        const isSelected = value === cat.value;

        return (
          <button
            key={cat.value}
            type="button"
            data-selected={isSelected}
            onClick={() => onChange(cat.value)}
            className={`
              group relative flex items-start gap-4 p-5 rounded-xl border-2 text-left
              transition-all duration-200 hover-elevate outline-none
              focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2
              ${cat.accent}
              ${
                isSelected
                  ? "shadow-md"
                  : "border-border bg-card hover:border-muted-foreground/20"
              }
            `}
          >
            {/* Icon container */}
            <div
              className={`
                w-11 h-11 rounded-xl flex items-center justify-center shrink-0
                transition-colors duration-200
                ${cat.iconAccent}
                ${!isSelected ? "bg-muted text-muted-foreground" : ""}
              `}
            >
              <Icon className="w-5 h-5" />
            </div>

            {/* Text */}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold leading-tight mb-1">
                {cat.label}
              </p>
              <p className="text-xs text-muted-foreground leading-relaxed">
                {cat.description}
              </p>
            </div>

            {/* Selection indicator */}
            {isSelected && (
              <div className="absolute top-3 right-3">
                <div className="w-5 h-5 rounded-full bg-primary flex items-center justify-center">
                  <svg className="w-3 h-3 text-primary-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              </div>
            )}
          </button>
        );
      })}
    </div>
  );
}
